import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import { fetchPracticeListRequest } from "../../redux/exam/examSlice";
import { setHomeworkData } from "../../redux/homework/HomeWorkSlice";
import DailyGoalModal from "../myspace/Engage/EngageProfile/DailyGoalModal";
import DashboardPageHeader from "./components/DashboardPageHeader";
import DailyGoalCards from "./components/DailyGoalCards";
import WeeklyActivity from "./components/WeeklyActivity";
import ResumeSection from "./components/ResumeSection";
import DashboardLeaderboard from "./components/DashboardLeaderboard";
import QuickActions from "./components/QuickActions";
import SubjectCollectionView from "../../containers/logged_user_components/user_dashboard/user_dashboard_content_component/dashboard_utlility_components/subject_collection_view/subject_collection_view";
import PracticeSublistModal from "./components/PracticeSublistModal";
import FocusTopics from "./components/FocusTopics";
import RecentTests from "./components/RecentTests";
import PerformanceOverview from "./components/PerformanceOverview";
import DailyPath from "./components/DailyPath";
import images from "../../utilities/images/images";
import {
  useFetchStudyDashboardDailyGoal,
  useRefreshStudyDashboardAfterGoalSave,
  useRefreshStudyDashboardResumeAndRecentTests,
  useStudyDashboardDailyGoalQuery,
  useStudyDashboardLeaderboardQuery,
  useStudyDashboardProgressQuery,
  useStudyDashboardResumeVideoQuery,
  useStudyDashboardScheduleContentQuery,
  useStudyDashboardScheduleWeekQuery,
  useStudyDashboardStreakQuery,
  useStudyDashboardTopWeakTopicsQuery,
  useStudyDashboardTestWiseAnalysisQuery,
  useStudyDashboardSubjectPerformanceQuery,
} from "./hooks/useStudyDashboardQueries";
import {
  normalizeDashboardResumeActivity,
} from "./utils/resumeUtils";
import {
  normalizeFocusTopics,
  normalizePerformanceSubjects,
  normalizeTestWiseRecentTests,
} from "./utils/dashboardBottomUtils";
import { usePracticeItemNavigation } from "./hooks/usePracticeItemNavigation";
import { useResumeItemNavigation } from "./hooks/useResumeItemNavigation";
import { useDailyPathNavigation } from "./hooks/useDailyPathNavigation";
import useStudyDashboardScrollRestore from "./hooks/useStudyDashboardScrollRestore";
import { buildQuickActionItems } from "./utils/practiceSectionUtils";
import { buildScheduleDay } from "./utils/dailyPathUtils";
import {
  getQuickActionAnchorId,
  readStudyDashboardScrollState,
  saveStudyDashboardScrollState,
} from "./utils/studyDashboardScrollRestore";
import uiActions from "../../redux/ui/action";
import "./StudyDashboard.css";

const StudyDashboard = () => {
  const history = useHistory();
  const dispatch = useDispatch();
  const practiceList = useSelector((state) => state.examSlice.practiceList);
  const userPreference = useSelector((state) => state.userPreference);
  const targetId = Number(userPreference?.targetDetails?.[0]?.target_id);
  const {
    handlePracticeItemClick,
    handleModalItemClick,
    closeModal,
    modalOpen,
    selectedSublist,
    selectedItemName,
  } = usePracticeItemNavigation();

  const [isDailyGoalModalOpen, setIsDailyGoalModalOpen] = useState(false);
  const [selectedScheduleDayKey, setSelectedScheduleDayKey] = useState("");
  const [highlightedQuickActionAnchorId, setHighlightedQuickActionAnchorId] =
    useState(() => readStudyDashboardScrollState()?.anchorId ?? null);
  const [restoredQuickActionPageIndex] = useState(
    () => readStudyDashboardScrollState()?.pageIndex ?? 0
  );

  const progressQuery = useStudyDashboardProgressQuery();
  const streakQuery = useStudyDashboardStreakQuery();
  const leaderboardQuery = useStudyDashboardLeaderboardQuery();
  const resumeVideoQuery = useStudyDashboardResumeVideoQuery();
  const topWeakTopicsQuery = useStudyDashboardTopWeakTopicsQuery();
  const testWiseAnalysisQuery = useStudyDashboardTestWiseAnalysisQuery();
  const subjectPerformanceQuery = useStudyDashboardSubjectPerformanceQuery();
  const dailyGoalQuery = useStudyDashboardDailyGoalQuery(isDailyGoalModalOpen);
  const scheduleWeekQuery = useStudyDashboardScheduleWeekQuery();

  const scheduleDays = scheduleWeekQuery.data ?? [];
  const selectedScheduleDay = useMemo(() => {
    const fromWeek =
      scheduleDays.find((day) => day.key === selectedScheduleDayKey) ?? null;
    if (fromWeek) return fromWeek;
    return buildScheduleDay(selectedScheduleDayKey);
  }, [scheduleDays, selectedScheduleDayKey]);

  useEffect(() => {
    if (!scheduleDays.length) return;
    setSelectedScheduleDayKey((current) => {
      if (current && scheduleDays.some((day) => day.key === current)) {
        return current;
      }
      return scheduleDays.find((day) => day.isToday)?.key || scheduleDays[0]?.key || "";
    });
  }, [scheduleDays]);

  const scheduleContentQuery = useStudyDashboardScheduleContentQuery({
    dayName: selectedScheduleDay?.dayName,
    rawDate: selectedScheduleDay?.rawDate,
    enabled: Boolean(selectedScheduleDay?.dayName && selectedScheduleDay?.rawDate),
  });

  const refreshAfterGoalSave = useRefreshStudyDashboardAfterGoalSave();
  const fetchDailyGoal = useFetchStudyDashboardDailyGoal();
  const refreshResumeAndRecentTests =
    useRefreshStudyDashboardResumeAndRecentTests();

  useEffect(() => {
    refreshResumeAndRecentTests();
  }, [refreshResumeAndRecentTests]);

  useEffect(() => {
    dispatch(fetchPracticeListRequest());
  }, [dispatch]);

  const progressData = progressQuery.data;
  const streakData = streakQuery.data;
  const leaderboardData = leaderboardQuery.data;
  const resumeVideo = resumeVideoQuery.data;
  const scheduleContent = scheduleContentQuery.data;
  const scheduleItems = scheduleContent?.items ?? [];
  const handleResume = useResumeItemNavigation();
  const { handleDailyPathItemClick } = useDailyPathNavigation();

  useEffect(() => {
    if (Array.isArray(scheduleContent?.homework)) {
      dispatch(setHomeworkData(scheduleContent.homework));
    }
  }, [dispatch, scheduleContent?.homework]);

  const isPageLoading =
    progressQuery.isLoading ||
    streakQuery.isLoading ||
    leaderboardQuery.isLoading ||
    resumeVideoQuery.isLoading;

  const quickActionItems = useMemo(
    () => buildQuickActionItems(practiceList, targetId),
    [practiceList, targetId]
  );

  const focusTopics = useMemo(
    () => normalizeFocusTopics(topWeakTopicsQuery.data),
    [topWeakTopicsQuery.data]
  );

  const recentTests = useMemo(
    () => normalizeTestWiseRecentTests(testWiseAnalysisQuery.data, 2),
    [testWiseAnalysisQuery.data]
  );

  const performanceSubjects = useMemo(
    () => normalizePerformanceSubjects(subjectPerformanceQuery.data),
    [subjectPerformanceQuery.data]
  );

  const needsQuickActionRestore =
    Boolean(highlightedQuickActionAnchorId) &&
    String(highlightedQuickActionAnchorId).startsWith("study-quick-action");
  const needsRecentTestRestore =
    Boolean(highlightedQuickActionAnchorId) &&
    (String(highlightedQuickActionAnchorId).startsWith("study-recent-test") ||
      highlightedQuickActionAnchorId === "study-recent-tests");

  useStudyDashboardScrollRestore({
    isReady:
      !isPageLoading &&
      (!needsQuickActionRestore || quickActionItems.length > 0) &&
      (!needsRecentTestRestore ||
        recentTests.length > 0 ||
        !testWiseAnalysisQuery.isLoading),
    restoreTrigger: `${quickActionItems.length}-${recentTests.length}`,
  });

  useEffect(() => {
    if (isPageLoading) {
      dispatch(uiActions.startLoading());
      return;
    }
    dispatch(uiActions.finishLoading());
  }, [dispatch, isPageLoading]);

  const goalSummary = progressData?.daily?.total_attempt ?? "";
  const progressPercent = String(progressData?.daily?.progress_percent ?? "").replace("%", "");

  const handleQuickActionClick = useCallback(
    (item, meta = {}) => {
      const anchorId = meta.anchorId || getQuickActionAnchorId(item?.id);
      setHighlightedQuickActionAnchorId(anchorId);
      saveStudyDashboardScrollState({
        anchorId,
        pageIndex: meta.pageIndex ?? 0,
      });
      handlePracticeItemClick(item);
    },
    [handlePracticeItemClick]
  );

  const resumeItems = useMemo(() => {
    return normalizeDashboardResumeActivity(resumeVideo).map((item) => ({
      ...item,
      icon: images.resume_note_icon,
    }));
  }, [resumeVideo]);

  const handleOpenDailyGoalModal = async () => {
    await fetchDailyGoal();
    setIsDailyGoalModalOpen(true);
  };

  if (isPageLoading) {
    return <div className="study-dashboard study-dashboard--loading" aria-busy="true" />;
  }

  return (
    <div className="study-dashboard">
      <DashboardPageHeader />

      <div className="study-dashboard__main-grid">
        <div className="study-dashboard__primary">
          <DailyGoalCards
            goalSummary={goalSummary}
            progressPercent={progressPercent}
            streakDays={Number(streakData?.streak_days) || 0}
            streakLabel={streakData?.streak_label || "Daily Goal Streak"}
            fireIconUrl={streakData?.action_icons?.fire_icon_url}
            onEditGoal={handleOpenDailyGoalModal}
            onOpenStreak={() => history.push("/my-space")}
          />

          <WeeklyActivity weekly={progressData?.weekly || {}} />
          <ResumeSection
            items={resumeItems}
            onResume={handleResume}
            isLoading={resumeVideoQuery.isLoading}
          />
        </div>

        <div className="study-dashboard__sidebar">
          <DashboardLeaderboard leaderboardData={leaderboardData} />
        </div>

        <div className="study-dashboard__quick-actions-wrap">
          <div className="study-dashboard__subject-collection">
            <SubjectCollectionView history={history} />
          </div>
          <QuickActions
            items={quickActionItems}
            onItemClick={handleQuickActionClick}
            highlightedAnchorId={highlightedQuickActionAnchorId}
            initialPageIndex={restoredQuickActionPageIndex}
          />
        </div>

        <div className="study-dashboard__bottom-left">
          <FocusTopics
            topics={focusTopics}
            isLoading={topWeakTopicsQuery.isLoading}
          />
          <RecentTests
            tests={recentTests}
            isLoading={testWiseAnalysisQuery.isLoading}
          />
          <PerformanceOverview
            subjects={performanceSubjects}
            isLoading={subjectPerformanceQuery.isLoading}
          />
        </div>

        <div className="study-dashboard__daily-path-wrap">
          <DailyPath
            scheduleDays={scheduleDays}
            scheduleItems={scheduleItems}
            selectedDayKey={selectedScheduleDayKey}
            onDayChange={setSelectedScheduleDayKey}
            onItemClick={handleDailyPathItemClick}
            isScheduleLoading={scheduleContentQuery.isFetching}
          />
        </div>
      </div>

      <DailyGoalModal
        isOpen={isDailyGoalModalOpen}
        onClose={() => setIsDailyGoalModalOpen(false)}
        dailyGoalData={dailyGoalQuery.data}
        onSaved={async (savedGoal) => {
          await refreshAfterGoalSave(savedGoal);
        }}
      />

      <PracticeSublistModal
        isOpen={modalOpen}
        onClose={closeModal}
        selectedItemName={selectedItemName}
        selectedSublist={selectedSublist}
        onSubitemClick={handleModalItemClick}
      />
    </div>
  );
};

export default StudyDashboard;

